// The wish create form's copy and chip lists.
//
// W45.0 — `route-size-ceiling.test.ts` flagged `routes/WishNew.tsx` for growing
// past its recorded baseline (15.5 KB → 16.0 KB), and `AGENTS.md §3 #10` says
// 「超就当场拆」: a baseline entry is a record of debt, not a licence to grow.
//
// **This file was not the cause.** It was already over when this round started,
// and the frontend is gitignored, so there is no history to prove it either way —
// the honest statement is that the debt was inherited and is being paid here
// rather than the baseline being edited to make a red test green.
//
// The tables lifted out are the ones with no behaviour: label maps, the
// category→template-hint copy, and the two chip lists derived from them. The
// component keeps its state, its mutations and its JSX.

import type { FilterChipItem } from "@/components/primitives/FilterChipGroup";
import { WISH_CATEGORIES, WISH_PRIORITIES } from "@/lib/api/wishlist";
import type { WishCategory, WishPriority, WishVisibility } from "@/lib/api/wishlist";
import { Eye, EyeOff } from "lucide-react";

export const CATEGORY_LABELS: Record<WishCategory, string> = {
  general: "一般",
  savings: "儲蓄",
  travel: "旅行",
  event: "事件",
  purchase: "購物",
  bucket_list: "願望清單",
  custom: "自訂",
};

export const PRIORITY_LABELS: Record<WishPriority, string> = {
  must: "必做",
  want: "想要",
  maybe: "也許",
};

// `general` + `bucket_list` + `custom` produce no default blocks per the
// backend `_CATEGORY_DEFAULT_BLOCKS` map (DATA_MODEL §2.1); the other 4
// each add 1-2 template blocks. Shown as helper text so users know what
// will happen after they hit submit.
export const CATEGORY_TEMPLATE_HINT: Record<WishCategory, string> = {
  general: "提交後可自行新增任何區塊",
  savings: "提交後預設 1 個「進度」區塊",
  travel: "提交後預設「行前準備」checklist + 「預算」budget",
  event: "提交後預設 1 個「備忘」區塊",
  purchase: "提交後預設「商品連結」link + 「預算」budget",
  bucket_list: "提交後可自行新增任何區塊",
  custom: "提交後可自行新增任何區塊",
};

export const CATEGORY_CHIPS: FilterChipItem[] = WISH_CATEGORIES.map((id) => ({
  id,
  label: CATEGORY_LABELS[id],
}));

export const PRIORITY_CHIPS: FilterChipItem[] = WISH_PRIORITIES.map((id) => ({
  id,
  label: PRIORITY_LABELS[id],
}));

// The icons are carried here rather than at the call site because the map's type
// is `typeof Eye`; leaving the values null and injecting them in JSX would make
// the table a lie that TypeScript cannot catch.
export const VISIBILITY_META: Record<
  WishVisibility,
  { label: string; icon: typeof Eye }
> = {
  shared: { label: "共享", icon: Eye },
  private: { label: "私人", icon: EyeOff },
};
